import { useEffect, useState } from "react";
import type {
  Lead,
  LeadInput,
  LeadRole,
  LeadSource,
  EntityExternalLink,
} from "@sales-ops-amplify/shared";
import { useCreateLead, useUpdateLead } from "@/hooks/useCompanies";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  ExternalLinksEditor,
  sanitizeExternalLinks,
} from "@/components/external-links-editor";

const ROLES: LeadRole[] = [
  "buyer",
  "champion",
  "influencer",
  "procurement",
  "referrer",
  "other",
];

const SOURCES: LeadSource[] = [
  "manual",
  "outreach",
  "demo",
  "referral",
  "apollo",
  "salesnav",
  "import",
  "hubspot",
  "salesforce",
];

const inputClass =
  "rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground";

type ContactFormState = {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  linkedinUrl: string;
  role: LeadRole;
  title: string;
  lifecycleStage: string;
  seniority: string;
  departments: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  relationship: string;
  source: LeadSource;
  notes: string;
  opportunityIds: string;
  externalLinks: EntityExternalLink[];
};

function emptyForm(defaults?: Partial<ContactFormState>): ContactFormState {
  return {
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    mobilePhone: "",
    linkedinUrl: "",
    role: "buyer",
    title: "",
    lifecycleStage: "",
    seniority: "",
    departments: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    relationship: "",
    source: "manual",
    notes: "",
    opportunityIds: "",
    externalLinks: [],
    ...defaults,
  };
}

function fromContact(c: Lead): ContactFormState {
  return emptyForm({
    name: c.name ?? "",
    firstName: c.firstName ?? "",
    lastName: c.lastName ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    mobilePhone: c.mobilePhone ?? "",
    linkedinUrl: c.linkedinUrl ?? "",
    role: c.role ?? "buyer",
    title: c.title ?? "",
    lifecycleStage: c.lifecycleStage ?? "",
    seniority: c.seniority ?? "",
    departments: (c.departments ?? []).join(", "),
    street: c.address?.street ?? "",
    city: c.city ?? c.address?.city ?? "",
    state: c.state ?? c.address?.state ?? "",
    postalCode: c.address?.postalCode ?? "",
    country: c.country ?? c.address?.country ?? "",
    relationship: c.relationship ?? "",
    source: (c.source as LeadSource) ?? "manual",
    notes: c.notes ?? "",
    opportunityIds: (c.opportunityIds ?? []).join(", "),
    externalLinks: c.externalLinks?.length
      ? c.externalLinks.map((l) => ({ ...l }))
      : [],
  });
}

function toInput(
  accountId: string,
  form: ContactFormState,
  linkedOpportunityId?: string,
): LeadInput {
  const departments = form.departments
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const opportunityIds = form.opportunityIds
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (linkedOpportunityId && !opportunityIds.includes(linkedOpportunityId)) {
    opportunityIds.push(linkedOpportunityId);
  }
  const name =
    form.name.trim() ||
    [form.firstName, form.lastName].filter(Boolean).join(" ").trim();

  return {
    accountId,
    name,
    firstName: form.firstName.trim() || undefined,
    lastName: form.lastName.trim() || undefined,
    email: form.email.trim() || undefined,
    phone: form.phone.trim() || undefined,
    mobilePhone: form.mobilePhone.trim() || undefined,
    linkedinUrl: form.linkedinUrl.trim() || undefined,
    role: form.role,
    title: form.title.trim() || undefined,
    lifecycleStage: form.lifecycleStage.trim() || undefined,
    seniority: form.seniority.trim() || undefined,
    departments: departments.length ? departments : undefined,
    address: {
      street: form.street.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      country: form.country.trim() || undefined,
    },
    city: form.city.trim() || undefined,
    state: form.state.trim() || undefined,
    country: form.country.trim() || undefined,
    relationship: form.relationship.trim() || undefined,
    source: form.source,
    notes: form.notes.trim() || undefined,
    opportunityIds: opportunityIds.length ? opportunityIds : undefined,
    externalLinks: sanitizeExternalLinks(form.externalLinks),
  };
}

function ContactFields({
  form,
  setForm,
  showOpportunityIds,
}: {
  form: ContactFormState;
  setForm: React.Dispatch<React.SetStateAction<ContactFormState>>;
  showOpportunityIds?: boolean;
}) {
  const set =
    (key: keyof ContactFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <input
        type="text"
        placeholder="Full name *"
        value={form.name}
        onChange={set("name")}
        className={inputClass}
        required
      />
      <select
        value={form.role}
        onChange={set("role")}
        className={inputClass}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {r.replace("_", " ")}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="First name"
        value={form.firstName}
        onChange={set("firstName")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Last name"
        value={form.lastName}
        onChange={set("lastName")}
        className={inputClass}
      />
      <input
        type="email"
        placeholder="Email"
        value={form.email}
        onChange={set("email")}
        className={inputClass}
      />
      <input
        type="url"
        placeholder="LinkedIn URL"
        value={form.linkedinUrl}
        onChange={set("linkedinUrl")}
        className={inputClass}
      />
      <input
        type="tel"
        placeholder="Phone"
        value={form.phone}
        onChange={set("phone")}
        className={inputClass}
      />
      <input
        type="tel"
        placeholder="Mobile phone"
        value={form.mobilePhone}
        onChange={set("mobilePhone")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Title"
        value={form.title}
        onChange={set("title")}
        className={`${inputClass} sm:col-span-2`}
      />
      <input
        type="text"
        placeholder="Seniority (e.g. VP, Director)"
        value={form.seniority}
        onChange={set("seniority")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Lifecycle stage"
        value={form.lifecycleStage}
        onChange={set("lifecycleStage")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Departments (comma-separated)"
        value={form.departments}
        onChange={set("departments")}
        className={`${inputClass} sm:col-span-2`}
      />
      <input
        type="text"
        placeholder="Relationship"
        value={form.relationship}
        onChange={set("relationship")}
        className={inputClass}
      />
      <select
        value={form.source}
        onChange={set("source")}
        className={inputClass}
      >
        {SOURCES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Street"
        value={form.street}
        onChange={set("street")}
        className={`${inputClass} sm:col-span-2`}
      />
      <input
        type="text"
        placeholder="City"
        value={form.city}
        onChange={set("city")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="State / region"
        value={form.state}
        onChange={set("state")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Postal code"
        value={form.postalCode}
        onChange={set("postalCode")}
        className={inputClass}
      />
      <input
        type="text"
        placeholder="Country"
        value={form.country}
        onChange={set("country")}
        className={inputClass}
      />
      {showOpportunityIds && (
        <input
          type="text"
          placeholder="Opportunity IDs (comma-separated)"
          value={form.opportunityIds}
          onChange={set("opportunityIds")}
          className={`${inputClass} sm:col-span-2`}
        />
      )}
      <textarea
        placeholder="Notes"
        value={form.notes}
        onChange={set("notes")}
        rows={2}
        className={`${inputClass} sm:col-span-2`}
      />
      <div className="sm:col-span-2">
        <ExternalLinksEditor
          value={form.externalLinks}
          onChange={(externalLinks) =>
            setForm((f) => ({ ...f, externalLinks }))
          }
        />
      </div>
    </div>
  );
}

interface ContactFormProps {
  accountId: string;
  opportunityId?: string;
  onCreated?: () => void;
}

export function LeadEditorForm({
  accountId,
  opportunityId,
  onCreated,
}: ContactFormProps) {
  const createPerson = useCreateLead();
  const [form, setForm] = useState(() => emptyForm());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input = toInput(accountId, form, opportunityId);
    if (!input.name) return;

    await createPerson.mutateAsync(input);
    setForm(emptyForm());
    onCreated?.();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Add person
      </h4>
      <ContactFields form={form} setForm={setForm} />
      <button
        type="submit"
        disabled={createPerson.isPending || !form.name.trim()}
        className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
      >
        {createPerson.isPending ? "Saving…" : "Save person"}
      </button>
    </form>
  );
}

interface ContactEditFormProps {
  contact: Lead;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function LeadEditForm({
  contact,
  onSaved,
  onCancel,
}: ContactEditFormProps) {
  const updatePerson = useUpdateLead();
  const [form, setForm] = useState(() => fromContact(contact));

  useEffect(() => {
    setForm(fromContact(contact));
  }, [contact]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input = toInput(contact.accountId, form);
    if (!input.name) return;
    const { accountId: _slug, ...updates } = input;
    await updatePerson.mutateAsync({ id: contact.id, updates });
    onSaved?.();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <ContactFields form={form} setForm={setForm} showOpportunityIds />
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={updatePerson.isPending || !form.name.trim()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {updatePerson.isPending ? "Saving…" : "Save changes"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

interface LeadListProps {
  leads: Array<{
    id: string;
    name: string;
    email?: string;
    phone?: string;
    mobilePhone?: string;
    linkedinUrl?: string;
    role?: string;
    title?: string;
    seniority?: string;
    departments?: string[];
    lifecycleStage?: string;
  }>;
  emptyMessage?: string;
}

export function LeadList({ leads, emptyMessage }: LeadListProps) {
  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(leads);

  if (leads.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {emptyMessage ?? "No people yet."}
      </p>
    );
  }

  return (
    <div>
      <div className="space-y-2">
        {pageItems.map((c) => (
          <div
            key={c.id}
            className="min-w-0 overflow-hidden rounded-md border border-border bg-card px-3 py-2 text-sm"
          >
            <div className="flex min-w-0 items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="break-words font-medium text-foreground">{c.name}</p>
                <p className="break-words text-xs text-muted-foreground">
                  {[c.role?.replace("_", " "), c.title].filter(Boolean).join(" · ")}
                </p>
                {(c.seniority || c.departments?.length || c.lifecycleStage) && (
                  <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
                    {[
                      c.seniority,
                      c.departments?.join(", "),
                      c.lifecycleStage ? `lifecycle: ${c.lifecycleStage}` : undefined,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                {c.email && (
                  <a
                    href={`mailto:${c.email}`}
                    className="text-xs text-brand hover:underline"
                  >
                    Email
                  </a>
                )}
                {(c.phone || c.mobilePhone) && (
                  <a
                    href={`tel:${c.phone ?? c.mobilePhone}`}
                    className="text-xs text-brand hover:underline"
                  >
                    Call
                  </a>
                )}
                {c.linkedinUrl && (
                  <a
                    href={c.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand hover:underline"
                  >
                    LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <PaginationBar
        page={page}
        pageSize={pageSize}
        totalItems={leads.length}
        totalPages={totalPages}
        showingFrom={showingFrom}
        showingTo={showingTo}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
